import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import { getAdminCategories, submitCategory, updateCategory } from "../../../../services/Admin/ProductsAndCategoriesApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import CreateCategoryDialog from "./CreateCategoryDialog";
import EditCategoryDialog from "./EditCategoryDialog";
import InactiveCategoriesTab from "./InactiveCategoriesTab";

const createdAtValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sortTreeByCreatedAt = (nodes = [], order = "desc") => {
  if (!Array.isArray(nodes)) return [];

  const sorted = [...nodes].sort((a, b) => {
    const diff = createdAtValue(b?.created_at) - createdAtValue(a?.created_at);
    return order === "asc" ? -diff : diff;
  });
  return sorted.map((node) => ({
    ...node,
    children_recursive: sortTreeByCreatedAt(node?.children_recursive || [], order),
  }));
};

const CategoriesTap = () => {
  const { i18n } = useTranslation();
  const lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  const isAr = lang.startsWith("ar");
  const navigate = useNavigate();

  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // new dialog state
  const [openDialog, setOpenDialog] = useState(false);
  // store current parent id for dialog (null means root)
  const [dialogParentId, setDialogParentId] = useState(null);

  // dialog state for edit
  const [editOpen, setEditOpen] = useState(false);
  const [editInitial, setEditInitial] = useState(null);
  const [statusTab, setStatusTab] = useState(0);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [activeSearch, setActiveSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    const fetchCats = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getAdminCategories();
        const list = res && res.success && Array.isArray(res.data) ? res.data : [];
        setCats(sortTreeByCreatedAt(list));
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/login");
          return;
        }
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetchCats();
  }, [navigate]);

  const getName = (c) => (isAr ? c.name_ar || c.name_en : c.name_en || c.name_ar);
  const getDesc = (c) => (isAr ? c.desc_ar || "" : c.desc_en || "");
  const isActiveStatus = (status) => status === 1 || status === "1" || status === true || status === "true";
  const matchesSearch = (cat, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [cat?.name_ar, cat?.name_en, cat?.desc_ar, cat?.desc_en]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const buildUpdateFormData = (cat, nextStatus) => {
    const formData = new FormData();
    formData.append("id", String(cat.id));
    if (cat.category_id !== undefined && cat.category_id !== null) {
      formData.append("category_id", String(cat.category_id));
    }
    formData.append("name_ar", cat.name_ar || "");
    formData.append("name_en", cat.name_en || "");
    formData.append("desc_ar", cat.desc_ar || "");
    formData.append("desc_en", cat.desc_en || "");
    // Backend expects a real file for image, not a stored path string.
    if (cat.image instanceof File || cat.image instanceof Blob) {
      formData.append("image", cat.image);
    }
    formData.append("status", String(nextStatus));
    return formData;
  };

  // new: format created_at
  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(); // adjust locale if needed
    } catch {
      return iso;
    }
  };

  const getChildrenCount = (node) => Array.isArray(node?.children_recursive) ? node.children_recursive.length : 0;

  // utility: insert created node under parentId recursively
  const insertUnderParent = (nodes, parentId, created) => {
    if (!parentId) return [created, ...nodes]; // root insert
    const walk = (list) =>
      list.map((n) => {
        if (n.id === parentId) {
          const children = Array.isArray(n.children_recursive) ? [...n.children_recursive] : [];
          return { ...n, children_recursive: [created, ...children] };
        }
        if (n.children_recursive && n.children_recursive.length) {
          return { ...n, children_recursive: walk(n.children_recursive) };
        }
        return n;
      });
    return walk(nodes);
  };

  // replace node by id recursively after update
  const replaceNodeById = (nodes, id, updated) => {
    return nodes.map((n) => {
      if (n.id === id) {
        return { ...n, ...updated };
      }
      if (n.children_recursive && n.children_recursive.length) {
        return { ...n, children_recursive: replaceNodeById(n.children_recursive, id, updated) };
      }
      return n;
    });
  };

  // open dialog for given parent (null for top-level)
  const openAddDialog = (parentId = null, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setDialogParentId(parentId);
    setOpenDialog(true);
  };

  // open edit dialog with node data
  const openEditDialog = (node, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setEditInitial(node);
    setEditOpen(true);
  };

  // handler: uses dialogParentId to know where to insert
  const handleSubmitCategory = async (formData) => {
    try {
      const res = await submitCategory(formData);
      const created = res && res.data ? res.data : res;
      if (created) {
        setCats((prev) => sortTreeByCreatedAt(insertUnderParent(prev, dialogParentId, created)));
      }
      return res;
    } catch (err) {
      throw err;
    } finally {
      // keep dialog open/close handled by dialog caller; parent will close dialog on success
    }
  };

  // handler for update: uses updateCategory API and updates tree
  const handleUpdateCategory = async (formData) => {
    try {
      const res = await updateCategory(formData);
      const updated = res && res.data ? res.data : res;
      if (updated && updated.id) {
        setCats((prev) => sortTreeByCreatedAt(replaceNodeById(prev, updated.id, updated)));
      }
      // close dialog by parent caller
      setEditOpen(false);
      return res;
    } catch (err) {
      throw err;
    }
  };

  const handleToggleCategoryStatus = async (cat, e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    if (!cat?.id) return;

    const nextStatus = isActiveStatus(cat.status) ? 0 : 1;
    const formData = buildUpdateFormData(cat, nextStatus);

    try {
      setStatusLoadingId(cat.id);
      const res = await updateCategory(formData);
      const updated = res && res.data ? res.data : res;
      if (updated && updated.id) {
        setCats((prev) => sortTreeByCreatedAt(replaceNodeById(prev, updated.id, updated)));
      }
      return res;
    } catch (err) {
      throw err;
    } finally {
      setStatusLoadingId(null);
    }
  };

  const filterActiveTreeBySearch = (nodes, query) => {
    if (!Array.isArray(nodes)) return [];

    return nodes.reduce((acc, node) => {
      if (!isActiveStatus(node?.status)) return acc;

      const children = Array.isArray(node?.children_recursive) ? node.children_recursive : [];
      const matchedChildren = filterActiveTreeBySearch(children, query);
      const selfMatched = matchesSearch(node, query);

      if (selfMatched || matchedChildren.length) {
        acc.push({ ...node, children_recursive: matchedChildren });
      }

      return acc;
    }, []);
  };

  const normalizedActiveSearch = activeSearch.trim();
  const orderedCats = sortTreeByCreatedAt(cats, sortOrder);
  const activeCategories = orderedCats.filter((cat) => isActiveStatus(cat?.status));
  const visibleActiveCategories = normalizedActiveSearch
    ? filterActiveTreeBySearch(activeCategories, normalizedActiveSearch)
    : activeCategories;

  // render children recursively with indentation
  const renderChildren = (children, level = 1) => {
    const activeChildren = Array.isArray(children) ? children.filter((child) => isActiveStatus(child?.status)) : [];
    if (!activeChildren.length) return null;
    return (
      <Box sx={{ mt: 1, pl: `${level * 2}px` }}>
        {activeChildren.map((child) => (
          <Box key={child.id} sx={{ display: "flex", mb: 1 }}>
            <Box sx={{ width: 56, height: 56, mr: 1, flexShrink: 0 }}>
              <img
                src={getImageUrl(child.image)}
                alt={getName(child)}
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, background: "#f5f5f5" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14 }}>
                    {getName(child)}
                  </Typography>
                  {(isAr ? child.desc_ar : child.desc_en) ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {isAr ? child.desc_ar : child.desc_en}
                    </Typography>
                  ) : null}
                  {/* created_at and children count for child */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {formatDate(child.created_at)} {` • `} {`الفئات الفرعية: ${getChildrenCount(child)}`}
                  </Typography>
                </Box>
                <Box>
                  <Button size="small" variant="outlined" onClick={(e) => openAddDialog(child.id, e)}>
                    إضافة فرعية
                  </Button>
                  <Button size="small" variant="text" onClick={(e) => openEditDialog(child, e)}>
                    تعديل
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    color={isActiveStatus(child.status) ? "error" : "success"}
                    disabled={statusLoadingId === child.id}
                    onClick={(e) => handleToggleCategoryStatus(child, e)}
                  >
                    {isActiveStatus(child.status) ? "إلغاء التفعيل" : "تفعيل"}
                  </Button>
                </Box>
              </Box>
              {renderChildren(child.children_recursive, level + 1)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={() => openAddDialog(null)}>
          إضافة فئة
        </Button>
      </Box>

      <CreateCategoryDialog
        open={openDialog}
        parentId={dialogParentId}
        onClose={() => setOpenDialog(false)}
        onSubmit={handleSubmitCategory}
      />
      <EditCategoryDialog
        open={editOpen}
        initialData={editInitial}
        onClose={() => setEditOpen(false)}
        onSubmit={handleUpdateCategory}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={statusTab} onChange={(e, v) => setStatusTab(v)}>
          <Tab label="كل الفئات" />
          <Tab label="الفئات غير النشطة" />
        </Tabs>
      </Box>

      {statusTab === 1 ? (
        <InactiveCategoriesTab
          categories={orderedCats}
          getName={getName}
          getDesc={getDesc}
          isActiveStatus={isActiveStatus}
          getImageUrl={getImageUrl}
          formatDate={formatDate}
          getChildrenCount={getChildrenCount}
          openAddDialog={openAddDialog}
          openEditDialog={openEditDialog}
          onToggleStatus={handleToggleCategoryStatus}
          statusLoadingId={statusLoadingId}
          isAr={isAr}
          sortOrder={sortOrder}
          onToggleSortOrder={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
        />
      ) : (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              fullWidth
              size="small"
              label="بحث في الفئات"
              placeholder="ابحث بالاسم أو الوصف"
              value={activeSearch}
              onChange={(e) => setActiveSearch(e.target.value)}
            />
            <Button
              variant="outlined"
              onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
              sx={{ whiteSpace: "nowrap" }}
            >
              {sortOrder === "desc" ? "الأحدث -> الأقدم" : "الأقدم -> الأحدث"}
            </Button>
          </Box>

          {!visibleActiveCategories.length ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">لا توجد نتائج مطابقة</Typography>
            </Box>
          ) : null}

          <Grid container spacing={3}>
            {visibleActiveCategories.map((cat) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={cat.id}>
              <Card sx={{ height: "100%", transition: "transform .18s", "&:hover": { transform: "translateY(-6px)", boxShadow: 4 } }}>
                <CardActionArea sx={{ display: "block", textAlign: isAr ? "right" : "left" }}>
                  <CardMedia
                    component="img"
                    image={getImageUrl(cat.image)}
                    alt={getName(cat)}
                    sx={{ height: 160, objectFit: "cover", backgroundColor: "#f5f5f5" }}
                  />
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {getName(cat)}
                        </Typography>
                        {getDesc(cat) ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {getDesc(cat)}
                          </Typography>
                        ) : null}
                        {/* created_at and children count for root category */}
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {formatDate(cat.created_at)} {` • `} {`الفئات الفرعية: ${getChildrenCount(cat)}`}
                        </Typography>
                      </Box>
                      <Box>
                        <Button size="small" variant="outlined" onClick={(e) => openAddDialog(cat.id, e)} sx={{ mr: 1 }}>
                          إضافة فرعية
                        </Button>
                        <Button size="small" variant="text" onClick={(e) => openEditDialog(cat, e)}>
                          تعديل
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          color={isActiveStatus(cat.status) ? "error" : "success"}
                          disabled={statusLoadingId === cat.id}
                          onClick={(e) => handleToggleCategoryStatus(cat, e)}
                        >
                          {isActiveStatus(cat.status) ? "إلغاء التفعيل" : "تفعيل"}
                        </Button>
                      </Box>
                    </Box>

                    {/* عرض الفئات الفرعية داخل نفس البطاقة */}
                    {renderChildren(cat.children_recursive)}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default CategoriesTap;
